import { useState, type ReactNode } from "react";
import Breadcrumb, { type Crumb } from "@/components/base/Breadcrumb";
import SortTabs from "@/components/base/SortTabs";
import FilterPanel, { type FilterGroup } from "@/components/feature/FilterPanel";

interface CategoryShellProps {
  crumbs: Crumb[];
  eyebrow: string;
  title: string;
  description: string;
  icon: string;
  filterGroups: FilterGroup[];
  selected: Record<string, string[]>;
  onToggle: (key: string, value: string) => void;
  onReset: () => void;
  sortOptions: string[];
  sort: string;
  onSort: (value: string) => void;
  resultLabel: string;
  children: ReactNode;
}

export default function CategoryShell({
  crumbs,
  eyebrow,
  title,
  description,
  icon,
  filterGroups,
  selected,
  onToggle,
  onReset,
  sortOptions,
  sort,
  onSort,
  resultLabel,
  children,
}: CategoryShellProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <>
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-6 md:py-8">
          <Breadcrumb items={crumbs} />
          <div className="mt-4 flex items-start gap-3">
            <span className="flex h-12 w-12 flex-none items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className={`${icon} text-2xl`}></i>
            </span>
            <div>
              <p className="font-label text-xs font-medium uppercase tracking-widest text-accent-600">
                {eyebrow}
              </p>
              <h1 className="text-2xl font-bold text-foreground-950 md:text-3xl">{title}</h1>
              <p className="mt-1.5 max-w-3xl text-sm leading-relaxed text-foreground-600">
                {description}
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6">
        <div className="flex gap-8">
          <aside className="hidden w-64 flex-none lg:block">
            <div className="sticky top-28 rounded-lg border border-background-200 bg-background-50 p-5">
              <FilterPanel
                groups={filterGroups}
                selected={selected}
                onToggle={onToggle}
                onReset={onReset}
              />
            </div>
          </aside>

          <div className="min-w-0 flex-1">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setDrawerOpen(true)}
                  className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700 lg:hidden"
                >
                  <i className="ri-filter-3-line"></i>
                  筛选
                </button>
                <p className="text-sm text-foreground-500">{resultLabel}</p>
              </div>
              <SortTabs options={sortOptions} value={sort} onChange={onSort} />
            </div>

            {children}
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <button
            type="button"
            aria-label="关闭筛选"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 cursor-pointer bg-foreground-950/40"
          ></button>
          <div className="absolute bottom-0 left-0 right-0 max-h-[80vh] overflow-y-auto rounded-t-lg bg-background-50 p-5">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-base font-bold text-foreground-950">筛选条件</h2>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-600"
                aria-label="关闭"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <FilterPanel
              groups={filterGroups}
              selected={selected}
              onToggle={onToggle}
              onReset={onReset}
            />
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              className="mt-5 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 py-3 text-sm font-medium text-background-50"
            >
              查看结果
            </button>
          </div>
        </div>
      )}
    </>
  );
}